<template>
  <article
    class="comment"
    :class="{ 'is-deleted': isHidden }"
  >
    <header class="comment__header">
      <h2 class="subheading">
        {{ truncateTitle }}
      </h2>

      <section class="comment__status_icons">
        <figure
          class="comment__replies"
          :title="
            $t('tooltips.REPLIES', { number: comment.commentRepliesCount })
          "
          v-if="comment.commentRepliesCount"
        >
          <Icon
            class="comment__replies_image"
            name="replies"
          />
          <figcaption class="comment__replies_number">
            {{ comment.commentRepliesCount }}
          </figcaption>
        </figure>
      </section>
    </header>
    <section class="comment__subheader">
      <p class="copy small">
        {{ comment.customerName }}
      </p>
    </section>
    <section class="comment__body">
      <img
        class="comment__picture"
        :src="profile.picture"
        :alt="$t('accessibility.ALT_PROFILE_PIC', { name: profile.name })"
      >
      <section class="comment__content">
        <h3 class="comment__author small-heading">
          <router-link
            class="link"
            :to="profile.route"
            :title="$t('tooltips.HOME')"
            :aria-label="$t('accessibility.ARIA_HOME')"
          >
            {{ profile.name }}
          </router-link>
          <span class="comment__timestamp small">-&nbsp;{{ timeSince(comment.createdAt) }}</span>
        </h3>
        <p class="comment__text copy">
          {{ truncateText }}
          <span
            v-if="textOverflow"
            class="copy link"
            @click="showHideText"
            @keyup.enter="showHideText"
            tabindex="0"
          >{{ buttonText }}</span>
        </p>
      </section>
    </section>
    <section
      class="comment__reactions"
      v-if="comment.reactions"
    >
      <template v-for="reaction in activeReactions">
        <Icon
          :key="`${reaction.type}-${comment.id}`"
          :title="translateReaction(reaction.type, reaction.count)"
          :name="reaction.type"
        />
        <span
          :key="`${reaction.type}-${comment.id}-number`"
          class="comment__reactions_count copy bold"
        >
          {{ reaction.count }}
        </span>
      </template>
    </section>
    <footer class="comment__footer">
      <section class="btn__group">
        <a
          v-if="comment.url"
          class="btn__action"
          role="link"
          :href="articleUrl"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="$t('accessibility.ARIA_TO_ARTICLE')"
          :title="comment.url"
        >
          {{ $t("buttons.TO_ARTICLE") }}
        </a>
      </section>
    </footer>
    <InfoBox
      v-if="isHidden"
      :content="$t(moderation)"
    />
  </article>
</template>

<script>
import { timeSince } from "@/helpers/functions";
import Icon from "./Icon.vue";
import InfoBox from "./InfoBox";

export default {
  name: "Comment",
  components: {
    Icon,
    InfoBox,
  },
  props: {
    comment: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isAlias: false,
      showMore: false,
    };
  },
  methods: {
    showHideText() {
      this.showMore = !this.showMore;
    },
    translateReaction(name, count) {
      const translation = this.$t(`reactions.${name.toUpperCase()}`);
      return `${translation} (${count})`;
    },
    timeSince,
  },
  computed: {
    activeReactions() {
      const reactionsObj = JSON.parse(this.comment.reactions);
      return reactionsObj.filter((reaction) => reaction.count > 0);
    },
    articleUrl() {
      return `${this.comment.url}?utm_source=ifragasatt&utm_medium=mitt.ifragasatt.se`;
    },
    buttonText() {
      return !this.showMore
        ? `...(${this.$t("buttons.SHOW_MORE")})`
        : `(${this.$t("buttons.SHOW_LESS")})`;
    },
    iconColor() {
      if (this.comment.status !== "hidden") return ["#46738E", "#fff"];
      return ["#ef455c", "#fff"];
    },
    isHidden() {
      return this.comment.status === "hidden";
    },
    moderation() {
      if (!this.comment.modMessage) return "placeholders.EMPTY_MOD_MESSAGE";
      return this.comment.modMessage;
    },
    profile() {
      if (this.comment.aliasAuthor?.name) {
        return {
          name: this.comment.aliasAuthor.name,
          picture: this.comment.aliasAuthor.profilePicture,
          route: `user?slug=${this.comment.aliasAuthor.slug}&isalias=true`,
        };
      }
      if (this.comment.alias?.name) {
        return {
          name: this.comment.alias.name,
          picture: this.comment.alias.profilePicture,
          route: `user?slug=${this.comment.alias.slug}&isalias=true`,
        };
      }
      return {
        name: `${this.comment.user.firstName} ${this.comment.user.lastName}`,
        picture: this.comment.user.profilePicture,
        route: `user?slug=${this.comment.user.slug}`,
      };
    },
    sumReactions() {
      const reactionsObj = JSON.parse(this.comment.reactions);
      return reactionsObj
        .map((reaction) => reaction.count)
        .reduce((prev, next) => prev + next);
    },
    textOverflow() {
      return this.comment.text.length > 200;
    },
    truncateText() {
      const str = this.comment.text;
      const trunc = str.substring(0, str.lastIndexOf(" ", 250));

      if (str.length <= 250) return str;

      if (!this.showMore) return trunc;

      return str;
    },
    truncateTitle() {
      const str = this.comment.title;
      const trunc = str.substring(0, str.lastIndexOf(" ", 50));

      if (!str.length) return this.$t("placeholders.NO_TITLE");
      if (str.length > 50) return `${trunc}...`;
      return str;
    },
  },
};
</script>
